import * as React from 'react';
import { X, Trophy, Target, Clock, Gift, ChevronRight, Info } from 'lucide-react';
import type { Quest, QuestObjective } from '../../../../types/features';

interface QuestDetailsModalProps {
    quest: Quest;
    onClose: () => void;
}

export const QuestDetailsModal: React.FC<QuestDetailsModalProps> = ({ quest, onClose }) => {
    const getProgressPercentage = (currentProgress: number, threshold: number) => {
        return Math.min((currentProgress / threshold) * 100, 100);
    };

    const formatReward = () => {
        const { type, amount } = quest.reward;
        switch (type) {
            case 'game_coins':
                return `${amount.toLocaleString()} Coins`;
            case 'exp':
                return `${amount.toLocaleString()} XP`;
            case 'free_spins':
                return `${amount} Free Spins`;
            case 'money':
                return amount.toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
            default:
                return '';
        }
    };

    const getTimeLeft = () => {
        const end = new Date(quest.endDate);
        const now = new Date();
        const diff = end.getTime() - now.getTime();

        if (diff <= 0) return 'Expired';

        const days = Math.floor(diff / (1000 * 60 * 60 * 24));
        const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

        if (days > 0) return `${days}d ${hours}h left`;
        return `${hours}h left`;
    };

    return (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden animate-[slideIn_0.3s_ease-out]">
                {/* Header */}
                <div className="relative">
                    {quest.bannerUrl && (
                        <div className="h-48">
                            <img
                                src={quest.bannerUrl}
                                alt={quest.name}
                                className="w-full h-full object-cover"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
                        </div>
                    )}
                    <button
                        onClick={onClose}
                        className="absolute top-4 right-4 text-white/80 hover:text-white transition-colors rounded-full p-1 hover:bg-black/10"
                    >
                        <X className="w-6 h-6" />
                    </button>
                    <div className="absolute bottom-4 left-4 right-4">
                        <h2 className="text-2xl font-bold text-white mb-2">{quest.name}</h2>
                        <p className="text-white/90">{quest.description}</p>
                    </div>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Status */}
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <Trophy className="w-5 h-5 text-yellow-500" />
                            <span className="font-medium">Quest Status</span>
                        </div>
                        <div className={`px-3 py-1 rounded-full text-sm font-medium
                            ${quest.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}`}
                        >
                            {quest.status.charAt(0).toUpperCase() + quest.status.slice(1)}
                        </div>
                    </div>

                    {/* Time */}
                    <div className="flex items-center justify-between py-4 border-t">
                        <div className="space-y-1">
                            <div className="text-sm text-gray-500">Start Date</div>
                            <div className="font-medium">{new Date(quest.startDate).toLocaleDateString()}</div>
                        </div>
                        <div className="h-0.5 flex-1 mx-4 bg-gray-100 relative">
                            <div
                                className="absolute inset-y-0 left-0 bg-blue-500"
                                style={{
                                    width: `${Math.min(
                                        100,
                                        Math.max(
                                            0,
                                            ((new Date().getTime() - new Date(quest.startDate).getTime()) /
                                                (new Date(quest.endDate).getTime() - new Date(quest.startDate).getTime())) *
                                            100
                                        )
                                    )}%`,
                                }}
                            />
                        </div>
                        <div className="space-y-1 text-right">
                            <div className="text-sm text-gray-500">End Date</div>
                            <div className="font-medium">{new Date(quest.endDate).toLocaleDateString()}</div>
                        </div>
                    </div>

                    {/* Objectives */}
                    <div className="space-y-4 border-t pt-4">
                        <h3 className="font-semibold flex items-center gap-2">
                            <Target className="w-5 h-5 text-blue-500" />
                            Objectives
                        </h3>
                        {quest.objectives.map((objective: QuestObjective) => (
                            <div key={objective.id} className="space-y-2">
                                <div className="flex items-center justify-between text-sm">
                                    <span className="text-gray-600">{objective.description}</span>
                                    <span className="font-medium">
                                        {objective.currentProgress}/{objective.threshold}
                                    </span>
                                </div>
                                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-blue-500 transition-all duration-300"
                                        style={{ width: `${getProgressPercentage(objective.currentProgress, objective.threshold)}%` }}
                                    />
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* Reward */}
                    <div className="border-t pt-4">
                        <h3 className="font-semibold flex items-center gap-2 mb-3">
                            <Gift className="w-5 h-5 text-emerald-500" />
                            Reward
                        </h3>
                        <div className="bg-emerald-50 text-emerald-700 px-4 py-3 rounded-lg flex items-center justify-between">
                            <span className="font-medium">{formatReward()}</span>
                            <span className="text-sm">{getTimeLeft()}</span>
                        </div>
                    </div>

                    {/* Additional Info */}
                    {quest.questGroupId && (
                        <div className="flex items-center gap-2 text-sm text-gray-500 border-t pt-4">
                            <Info className="w-4 h-4" />
                            <span>Part of a quest chain</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}; 